import { useMemo } from 'react';
import { DataTable, MoreFiltersMenu } from '@odo/shared-ui';
import { buildNotificationColumns } from './notificationColumns';
import {
    countPatientRows,
    expandNotificationRows,
    isNotificationRow,
    type NotificationTableRow
} from './notificationRows';
import {
    NotificationsEmptyState,
    NotificationsErrorState,
    NotificationsSkeletonRows
} from './NotificationsTableStates';
import { DM_INBOX_SEARCH_MAX_LENGTH } from '../../../services/dmNotifications';
import type { NotificationsInbox } from './useNotificationsInbox';

// Sub-rows are inert: DataTable paints every row clickable while a rowAction exists, so they opt out.
const NOTIFICATION_ROW_CLASS = 'bg-gray-50/60 !cursor-default hover:!bg-gray-50/60';
// Cell padding animates to zero alongside CollapsibleCell, so the whole row folds away.
const LEAVING_ROW_CLASS = '[&>td]:!py-0';

function rowClassName(row: NotificationTableRow): string | undefined {
    const classes = [
        isNotificationRow(row) ? NOTIFICATION_ROW_CLASS : null,
        row.leaving ? LEAVING_ROW_CLASS : null
    ];
    return classes.filter(Boolean).join(' ') || undefined;
}

export function NotificationsTable({ inbox }: { inbox: NotificationsInbox }) {
    const { table, query, loadMore, search, filters, hasFilters, clearFilters } = inbox;
    const { folds, leavingIds, hiddenIds, toggleExpanded, markLeaving } = inbox;
    const rows = useMemo(
        () =>
            query.isError
                ? []
                : expandNotificationRows(query.rows, { folds, leavingIds, hiddenIds }),
        [query.isError, query.rows, folds, leavingIds, hiddenIds]
    );
    const columns = useMemo(
        () =>
            buildNotificationColumns({
                rowCount: rows.length,
                onToggle: toggleExpanded,
                onReviewed: markLeaving,
                listSearch: table.listSearch,
                status: table.status
            }),
        [rows.length, toggleExpanded, markLeaving, table.listSearch, table.status]
    );
    const total = query.meta?.total ?? 0;
    const totalPages = Math.max(1, query.meta?.totalPages ?? 1);

    return (
        <DataTable
            label="DM notifications"
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.id}
            rowAction={{
                onActivate: (row) => {
                    if (!isNotificationRow(row)) toggleExpanded(row.patient.patientId);
                },
                mode: 'click'
            }}
            rowClassName={rowClassName}
            rowHeightClassName=""
            sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
            mode={table.mode}
            onModeChange={table.setMode}
            {...loadMore}
            loadedCount={countPatientRows(rows)}
            pagination={{ page: table.page, totalPages, total, onPage: table.setPage }}
            isLoading={query.isLoading}
            loadingRows={<NotificationsSkeletonRows />}
            emptyState={
                query.isError ? (
                    <NotificationsErrorState onRetry={() => void query.refresh()} />
                ) : (
                    <NotificationsEmptyState
                        filtersActive={hasFilters}
                        onClearFilters={clearFilters}
                        closed={table.status === 'closed'}
                    />
                )
            }
            emptyStateCellClassName="px-3 py-16"
            search={{
                ...search,
                placeholder: 'Search patient or notification title',
                maxLength: DM_INBOX_SEARCH_MAX_LENGTH
            }}
            filters={filters}
            toolbar={
                <MoreFiltersMenu
                    categories={inbox.more}
                    allCategories={inbox.more}
                    hiddenActiveCount={inbox.moreActiveCount}
                />
            }
            className="overflow-hidden px-8 pb-8"
        />
    );
}
